import { Icon } from '#/components/Icon/Icon';
import { IdealistLogo } from '#/components/Logo/IdealistLogo';
import { useI18n } from '#/i18n/hooks/useI18n';
import { I18nLocale } from '#/i18n/types/I18nLocale';
import { pageFooterDataByLocale } from '#/mappings/PageFooter/pageFooterDataByLocale';
import { homeRoute } from '#/routing/routes';
import { trackAnchorClicked } from '#/utils/analytics/track/trackAnchorClicked';
import { trackClicked } from '#/utils/analytics/track/trackClicked';

import { PageFooterCentralLinks } from './CentralLinks/PageFooterCentralLinks';
import { PageFooterLocaleLinks } from './LocaleLinks/PageFooterLocaleLinks';
import {
  PageFooterBottom,
  PageFooterCopyright,
  PageFooterHolder,
  PageFooterLogoLink,
  PageFooterLogoMissionHolder,
  PageFooterMissionStatement,
  PageFooterSectionExternalLink,
  PageFooterSectionHolder,
  PageFooterSectionLink,
  PageFooterSectionLinksHolder,
  PageFooterSectionTitle,
  PageFooterSectionsAndLogoMissionHolder,
  PageFooterSectionsHolder,
  PageFooterSocialLink,
  PageFooterSocialLinksHolder,
} from './PageFooter.styled';
import { PageFooterVolunteeringLinks } from './VolunteeringLinks/PageFooterVolunteeringLinks';
import {
  pageFooterDataByLinkType,
  pageFooterSocialLinks,
} from './pageFooterConstants';
import { pageFooterOrderedSectionsMetadata } from './pageFooterOrderedSectionsMetadata';

type Props = {
  urlByLocale: Record<I18nLocale, string | undefined> | undefined;
  showDivider: boolean;
};

export function PageFooter({ urlByLocale, showDivider }: Props) {
  const { i18n, locale } = useI18n();

  const data = pageFooterDataByLocale[locale];

  return (
    <PageFooterHolder data-qa-id="footer" $showDivider={showDivider}>
      <PageFooterSectionsAndLogoMissionHolder $showDivider={showDivider}>
        <PageFooterSectionsHolder>
          {pageFooterOrderedSectionsMetadata
            .map(({ sectionName }) => {
              const section = data[sectionName];

              if (!section) return null;

              return (
                <PageFooterSectionHolder
                  key={sectionName}
                  $isEsOrPt={
                    locale === I18nLocale.ES || locale === I18nLocale.PT
                  }
                >
                  <PageFooterSectionTitle>
                    {section.title}
                  </PageFooterSectionTitle>

                  <PageFooterSectionLinksHolder>
                    {section.links.map((link) => {
                      if (!pageFooterDataByLinkType[link.type]) {
                        return null;
                      }
                      const { trackClickedName } =
                        pageFooterDataByLinkType[link.type];

                      if (link.href.startsWith('https://')) {
                        return (
                          <PageFooterSectionExternalLink
                            key={link.title}
                            href={link.href}
                            onClick={() => {
                              trackClicked(trackClickedName, {
                                context: 'Footer',
                              });
                            }}
                            target="_blank"
                          >
                            {link.title}
                          </PageFooterSectionExternalLink>
                        );
                      } else {
                        return (
                          <PageFooterSectionLink
                            key={link.title}
                            to={link.href}
                            onClick={() => {
                              trackClicked(trackClickedName, {
                                context: 'Footer',
                              });
                            }}
                          >
                            {link.title}
                          </PageFooterSectionLink>
                        );
                      }
                    })}
                  </PageFooterSectionLinksHolder>
                </PageFooterSectionHolder>
              );
            })
            .filter(Boolean)}
        </PageFooterSectionsHolder>

        <PageFooterLogoMissionHolder $showDivider={showDivider}>
          <PageFooterLogoLink
            to={homeRoute.with({ lang: locale })}
            onClick={() => trackClicked('Footer Logo')}
          >
            <IdealistLogo withText height={56} />
          </PageFooterLogoLink>

          <PageFooterMissionStatement>
            {data.missionStatement}
          </PageFooterMissionStatement>

          <PageFooterSocialLinksHolder>
            {pageFooterSocialLinks(i18n)[locale].map(
              ({ href, eventName, iconName }) => (
                <PageFooterSocialLink
                  type="link"
                  nativeAnchorTag
                  href={href}
                  key={iconName}
                  onClick={(event) => trackAnchorClicked(event, eventName)}
                  aria-label={eventName}
                >
                  <Icon name={iconName} size={20} colorName="white" />
                </PageFooterSocialLink>
              ),
            )}
          </PageFooterSocialLinksHolder>
        </PageFooterLogoMissionHolder>
      </PageFooterSectionsAndLogoMissionHolder>

      <PageFooterBottom>
        <PageFooterLocaleLinks urlByLocale={urlByLocale} />

        {locale === I18nLocale.EN && <PageFooterCentralLinks />}

        <PageFooterCopyright>
          {i18n('Copyright © %(year)s Idealist.org', {
            year: new Date().getFullYear(),
          })}
        </PageFooterCopyright>
      </PageFooterBottom>

      {locale === I18nLocale.EN && <PageFooterVolunteeringLinks />}
    </PageFooterHolder>
  );
}
